import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, FileText, FileSpreadsheet, Search, User,
  AlertCircle, CheckCircle, ChevronRight,
  Calendar, X, Eye, Loader2, Users, IndianRupee, Clock, ClipboardList, BarChart3, Briefcase, Landmark
} from 'lucide-react';
import {
  fetchPayrollRuns,
  fetchDashboardKpis,
  fetchReportPreview,
  fetchSalaryStatementPreview,
  searchEmployees,
  fetchEmployeeLoans,
  fetchHrReport,
  downloadAuthenticatedFile,
  HR_REPORT_CONFIG,
} from '../../services/reportsApi';
import PCBPaybillReportView from './PCBPaybillReportView';
import PCBPaybillSummaryView from './PCBPaybillSummaryView';
import PCBGrandSummaryView from './PCBGrandSummaryView';
import PCBLedgerView from './PCBLedgerView';
import HeadWiseReportView from './HeadWiseReportView';
import HeadWiseReportDetailView from './HeadWiseReportDetailView';
import PCBBroadSheetView from './PCBBroadSheetView';
import PfsVpfReportView from './PfsVpfReportView';
import PfaReportView from './PfaReportView';
import CpfReportView from './CpfReportView';
import CpfContributionReportView from './CpfContributionReportView';
import CpfMissingPranReportView from './CpfMissingPranReportView';
import GisReportView from './GisReportView';
import SbiGmReportView from './SbiGmReportView';
import StrengthReportView from './StrengthReportView';
import { PCBWageSlipView } from './PCBPaybillReportView';
import RetiredEmployeesReportView from './RetiredEmployeesReportView';
import PastRetiredEmployeesReportView from './PastRetiredEmployeesReportView';
import ServiceDurationReportView from './ServiceDurationReportView';
import AdvReportView from './AdvReportView';
import FinalSettlementView from './FinalSettlementView';
import SalaryPaymentSummaryView from './SalaryPaymentSummaryView';
import FdInterestReportView from './FdInterestReportView';
import BankLoanSummaryReportView from './BankLoanSummaryReportView';
import { buildApiUrl } from '../../utils/apiSetup';

const PAGE_SIZE = 25;

const MAKER_HR_REPORT_IDS = ['attendance-register', 'leave-register', 'employee-master'];
const CHECKER_HR_REPORT_IDS = ['attendance-register', 'leave-register', 'employee-master', 'loan-advance', 'compliance-summary'];

const Reports = ({ reportScope }) => {
  const showPayrollReports = !reportScope || reportScope === 'checker';
  const showLoanReportTools = !reportScope || reportScope === 'checker';
  const visibleHrReports = useMemo(() => {
    if (reportScope === 'maker') {
      return HR_REPORT_CONFIG.filter((r) => MAKER_HR_REPORT_IDS.includes(r.id));
    }
    if (reportScope === 'checker') {
      return HR_REPORT_CONFIG.filter((r) => CHECKER_HR_REPORT_IDS.includes(r.id));
    }
    return HR_REPORT_CONFIG;
  }, [reportScope]);
  const [approvedRuns, setApprovedRuns] = useState([]);
  const [selectedPayrollRun, setSelectedPayrollRun] = useState(null);
  const [selectedPaybillDept, setSelectedPaybillDept] = useState('ALL');
  const [selectedPaybillMonth, setSelectedPaybillMonth] = useState('ALL');
  const [selectedPaybillYear, setSelectedPaybillYear] = useState('ALL');
  const [departmentsList, setDepartmentsList] = useState([]);
  const { showSuccess, showError } = useNotification();
  const setError = (msg) => { if (msg) showError(msg); };
  const setSuccess = (msg) => { if (msg) showSuccess(msg); };
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const res = await fetch(buildApiUrl('/api/v1/departments'), {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setDepartmentsList(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
      }
    };
    fetchDepartments();
  }, []);

  // Preview states
  const [previewType, setPreviewType] = useState(null); // 'synopsis', 'salary-statement'
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedSector, setSelectedSector] = useState(null);
  const [availableSectors, setAvailableSectors] = useState([]);

  // Employee Payslip Download State
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employees, setEmployees] = useState([]);
  const [employeeLoading, setEmployeeLoading] = useState(false);

  // Employee Loan Report State
  const [loanReportSearch, setLoanReportSearch] = useState('');
  const [loanReportEmployees, setLoanReportEmployees] = useState([]);
  const [loanReportLoading, setLoanReportLoading] = useState(false);
  const [selectedLoanEmployee, setSelectedLoanEmployee] = useState(null);
  const [employeeLoans, setEmployeeLoans] = useState([]);
  const [selectedLoanType, setSelectedLoanType] = useState('');
  const [loanStartDate, setLoanStartDate] = useState('');
  const [loanEndDate, setLoanEndDate] = useState('');
  const [loansLoading, setLoansLoading] = useState(false);

  // Dashboard KPIs
  const [dashboardKpis, setDashboardKpis] = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);

  // HR report table state
  const [activeHrReport, setActiveHrReport] = useState(null);
  const [hrReportData, setHrReportData] = useState({ items: [], total: 0 });
  const [hrReportLoading, setHrReportLoading] = useState(false);
  const [hrReportPage, setHrReportPage] = useState(0);
  const [hrReportSearch, setHrReportSearch] = useState('');
  const [hrReportSort, setHrReportSort] = useState({ key: null, dir: 'desc' });
  const [viewingPayslip, setViewingPayslip] = useState(false);
  const [viewingPaybillSummary, setViewingPaybillSummary] = useState(false);
  const [viewingGrandSummary, setViewingGrandSummary] = useState(false);
  const [viewingLedger, setViewingLedger] = useState(false);
  const [viewingHeadWisePayslip, setViewingHeadWisePayslip] = useState(false);
  const [viewingHeadWiseSummary, setViewingHeadWiseSummary] = useState(false);
  const [viewingHeadWiseReport, setViewingHeadWiseReport] = useState(false);
  const [viewingBroadSheet, setViewingBroadSheet] = useState(false);
  const [viewingPfsVpf, setViewingPfsVpf] = useState(false);
  const [viewingPfa, setViewingPfa] = useState(false);
  const [viewingCpf, setViewingCpf] = useState(false);
  const [viewingCpfContribution, setViewingCpfContribution] = useState(false);
  const [viewingCpfMissingPran, setViewingCpfMissingPran] = useState(false);
  const [viewingGis, setViewingGis] = useState(false);
  const [viewingSbiGm, setViewingSbiGm] = useState(false);
  const [viewingStrength, setViewingStrength] = useState(false);
  const [viewingWageSlip, setViewingWageSlip] = useState(false);
  const [viewingRetiredReports, setViewingRetiredReports] = useState(false);
  const [viewingPastRetiredReports, setViewingPastRetiredReports] = useState(false);
  const [viewingServiceDuration, setViewingServiceDuration] = useState(false);
  const [viewingAdvReport, setViewingAdvReport] = useState(false);
  const [viewingFinalSettlement, setViewingFinalSettlement] = useState(false);
  const [viewingSalaryPaymentSummary, setViewingSalaryPaymentSummary] = useState(false);
  const [viewingFdInterest, setViewingFdInterest] = useState(false);
  const [viewingBankLoanSummary, setViewingBankLoanSummary] = useState(false);

  const kpisAbortRef = useRef(null);
  const hrReportAbortRef = useRef(null);
  const employeeSearchAbortRef = useRef(null);
  const loanSearchAbortRef = useRef(null);

  useEffect(() => {
    fetchPayrollRunsData();
  }, []);

  const fetchPayrollRunsData = async () => {
    try {
      const runs = await fetchPayrollRuns();
      const allRuns = Array.isArray(runs) ? runs : [];
      const approved = allRuns.filter((run) =>
        ['approved', 'processed', 'paid', 'completed'].includes(String(run.status || '').toLowerCase())
      );
      const targetRuns = approved.length > 0 ? approved : allRuns;
      setApprovedRuns(targetRuns);

      if (targetRuns.length > 0) {
        const sorted = [...targetRuns].sort((a, b) => {
          const dateA = (a.payroll_year || 0) * 12 + (a.payroll_month || 0);
          const dateB = (b.payroll_year || 0) * 12 + (b.payroll_month || 0);
          return dateB - dateA;
        });
        setSelectedPayrollRun(sorted[0].id);
      }
    } catch (err) {
      console.error('Error fetching payroll runs:', err);
      setError('Failed to load payroll data');
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardKpis = useCallback(async (payrollRunId) => {
    if (kpisAbortRef.current) kpisAbortRef.current.abort();
    const controller = new AbortController();
    kpisAbortRef.current = controller;
    setKpisLoading(true);
    try {
      const data = await fetchDashboardKpis(payrollRunId, controller.signal);
      if (!controller.signal.aborted) setDashboardKpis(data);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error fetching KPIs:', err);
      }
    } finally {
      if (!controller.signal.aborted) setKpisLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardKpis(selectedPayrollRun || undefined);
  }, [selectedPayrollRun, loadDashboardKpis]);

  const getSelectedRun = () => {
    return approvedRuns.find(r => r.id === selectedPayrollRun);
  };

  const getMonthName = (month) => {
    return new Date(2025, month - 1).toLocaleString('default', { month: 'long' });
  };

  const extractErrorMessage = async (response, fallback) => {
    try {
      const data = await response.json();
      if (typeof data.detail === 'string') return data.detail;
      if (Array.isArray(data.detail) && data.detail.length > 0) {
        return data.detail[0].msg || fallback;
      }
    } catch (error) {
      return fallback;
    }

    return fallback;
  };

  // Fetch preview data
  const fetchPreview = async (type) => {
    if (!selectedPayrollRun) return;

    setPreviewLoading(true);
    setPreviewType(type);
    setPreviewData(null);

    try {
      if (type === 'synopsis') {
        const data = await fetchReportPreview(selectedPayrollRun);
        setPreviewData(data.synopsis);
        setAvailableSectors(
          (data.available_sectors || []).map((sector) => ({
            code: sector.code,
            department_name: sector.name,
          }))
        );
      } else if (type === 'salary-statement' && selectedSector) {
        const data = await fetchSalaryStatementPreview(selectedPayrollRun, selectedSector);
        setPreviewData(data);
      }
    } catch (err) {
      console.error('Error fetching preview:', err);
      setError(err.message || 'Failed to load preview');
      setTimeout(() => setError(''), 3000);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Load sectors when synopsis is requested
  const handleSynopsisPreview = async () => {
    await fetchPreview('synopsis');
  };

  // Load salary statement when sector changes
  useEffect(() => {
    if (previewType === 'salary-statement' && selectedSector) {
      fetchPreview('salary-statement');
    }
  }, [selectedSector]);

  const closePreview = () => {
    setPreviewType(null);
    setPreviewData(null);
    setSelectedSector(null);
  };

  // Search employees for payslip download
  const searchEmployeesForPayslip = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setEmployees([]);
      return;
    }

    if (employeeSearchAbortRef.current) employeeSearchAbortRef.current.abort();
    const controller = new AbortController();
    employeeSearchAbortRef.current = controller;

    setEmployeeLoading(true);
    try {
      const data = await searchEmployees(searchTerm, controller.signal);
      if (!controller.signal.aborted) setEmployees(data);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error searching employees:', err);
    } finally {
      if (!controller.signal.aborted) setEmployeeLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchEmployeesForPayslip(employeeSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [employeeSearch]);

  // Search employees for loan report
  const searchLoanReportEmployees = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setLoanReportEmployees([]);
      return;
    }

    if (loanSearchAbortRef.current) loanSearchAbortRef.current.abort();
    const controller = new AbortController();
    loanSearchAbortRef.current = controller;

    setLoanReportLoading(true);
    try {
      const data = await searchEmployees(searchTerm, controller.signal);
      if (!controller.signal.aborted) setLoanReportEmployees(data);
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Error searching employees for loan report:', err);
    } finally {
      if (!controller.signal.aborted) setLoanReportLoading(false);
    }
  };

  // Debounced search for loan report
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchLoanReportEmployees(loanReportSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [loanReportSearch]);

  // Fetch loans for selected employee
  const fetchEmployeeLoansData = async (employeeId) => {
    setLoansLoading(true);
    try {
      const data = await fetchEmployeeLoans(employeeId);
      setEmployeeLoans(data.loans || []);
      if (data.loans && data.loans.length > 0) {
        setSelectedLoanType(data.loans[0].loan_type);
      }
    } catch (err) {
      console.error('Error fetching employee loans:', err);
      setEmployeeLoans([]);
    } finally {
      setLoansLoading(false);
    }
  };

  // Select employee for loan report
  const selectEmployeeForLoanReport = (emp) => {
    setSelectedLoanEmployee(emp);
    setLoanReportSearch('');
    setLoanReportEmployees([]);
    fetchEmployeeLoansData(emp.id);

    // Set default date range (current financial year)
    const now = new Date();
    const currentYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    setLoanStartDate(`${currentYear}-04-01`);
    setLoanEndDate(`${currentYear + 1}-03-31`);
  };

  // Download loan report
  const downloadLoanReport = async () => {
    if (!selectedLoanEmployee || !selectedLoanType || !loanStartDate || !loanEndDate) {
      setError('Please select employee, loan type, and date range');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      setSuccess('Downloading loan report...');
      const response = await fetch(buildApiUrl(`/api/v1/reports/employee-loan-report/${selectedLoanEmployee.id}/${selectedLoanType}?start_date=${loanStartDate}&end_date=${loanEndDate}&format=pdf`),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        const empName = selectedLoanEmployee.full_name || selectedLoanEmployee.emp_name || selectedLoanEmployee.emp_code;
        link.download = `Loan_Statement_${empName}_${selectedLoanType}_${loanStartDate}_to_${loanEndDate}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        setSuccess('Loan report downloaded successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to download loan report');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error downloading loan report:', error);
      setError('Failed to download loan report');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Download employee payslip
  const downloadEmployeePayslip = async (employeeId, employeeName, empNo) => {
    if (!selectedPayrollRun) {
      setError('Please select a payroll period first');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const run = getSelectedRun();
    if (!run) return;

    try {
      const response = await fetch(buildApiUrl(`/api/v1/benfish-payroll/admin/employee/${employeeId}/payslip?month=${run.payroll_month}&year=${run.payroll_year}`),
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `Salary_Slip_${empNo}_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);

        setSuccess(`Payslip downloaded for ${employeeName}`);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to download payslip');
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Error downloading payslip:', error);
      setError('Failed to download payslip');
      setTimeout(() => setError(''), 3000);
    }
  };


  const downloadReport = async (type, format) => {
    if (!selectedPayrollRun) return;

    const run = getSelectedRun();
    let url = '';
    let filename = '';

    if (type === 'synopsis') {
      url = `/api/v1/reports/synopsis/${selectedPayrollRun}?format=pdf`;
      filename = `Synopsis_Report_${run ? `${getMonthName(run.payroll_month)}_${run.payroll_year}` : 'report'}.pdf`;
    } else if (type === 'salary-statement') {
      url = `/api/v1/reports/salary-statement/${selectedPayrollRun}/${selectedSector}?format=${format}`;
      filename = `Salary_Statement_${selectedSector}_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
    } else if (type === 'pay-bill') {
      url = `/api/v1/reports/pay-bill/${selectedPayrollRun}`;
      filename = `PAY_BILL_${getMonthName(run.payroll_month)}_${run.payroll_year}.xlsx`;
    } else if (type === 'professional-tax') {
      url = `/api/v1/reports/professional-tax/${selectedPayrollRun}?format=pdf`;
      filename = `Professional_Tax_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
    } else if (type === 'income-tax') {
      url = `/api/v1/reports/income-tax/${selectedPayrollRun}?format=pdf`;
      filename = `Income_Tax_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
    } else if (type === 'pf-cpf') {
      url = `/api/v1/reports/pf-report/${selectedPayrollRun}?pf_type=gpf&format=pdf`;
      filename = `GPF_Report_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
    } else if (type === 'pay-bill-pdf') {
      url = `/api/v1/reports/pay-bill-pdf/${selectedPayrollRun}`;
      filename = `PAY_BILL_INNER_SHEET_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
    } else if (type === 'eps') {
      url = `/api/v1/reports/eps-report/${selectedPayrollRun}?format=pdf`;
      filename = `EPS_Report_${getMonthName(run.payroll_month)}_${run.payroll_year}.pdf`;
    }

    try {
      setSuccess(`Downloading ${type.replace('-', ' ')}...`);
      await downloadAuthenticatedFile(url, filename);
      setSuccess('Downloaded successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error downloading report:', err);
      setError(err.message || 'Failed to download report');
      setTimeout(() => setError(''), 3000);
    }
  };

  const getSelectedRunDateWindow = () => {
    const run = getSelectedRun();
    if (!run) return null;
    const month = String(run.payroll_month).padStart(2, '0');
    const year = String(run.payroll_year);
    const lastDay = new Date(run.payroll_year, run.payroll_month, 0).getDate();
    return {
      fromDate: `${year}-${month}-01`,
      toDate: `${year}-${month}-${String(lastDay).padStart(2, '0')}`,
    };
  };

  const activeHrReportConfig = useMemo(
    () => HR_REPORT_CONFIG.find((r) => r.id === activeHrReport),
    [activeHrReport]
  );

  const loadHrReport = useCallback(async (reportId, page = 0, search = '', sort = hrReportSort) => {
    if (!reportId) return;
    if (hrReportAbortRef.current) hrReportAbortRef.current.abort();
    const controller = new AbortController();
    hrReportAbortRef.current = controller;
    setHrReportLoading(true);

    const dateWindow = getSelectedRunDateWindow();
    const params = {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
      search: search || undefined,
      sort_dir: sort.dir,
    };
    if (sort.key) params.sort_by = sort.key;
    if (dateWindow && ['attendance-register', 'leave-register'].includes(reportId)) {
      params.from_date = dateWindow.fromDate;
      params.to_date = dateWindow.toDate;
    }

    try {
      const data = await fetchHrReport(reportId, params, controller.signal);
      if (!controller.signal.aborted) {
        setHrReportData({ items: data.items || [], total: data.total || 0 });
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Error loading HR report:', err);
        setError(err.message || 'Failed to load report');
        setTimeout(() => setError(''), 3000);
      }
    } finally {
      if (!controller.signal.aborted) setHrReportLoading(false);
    }
  }, [hrReportSort]);

  useEffect(() => {
    if (!activeHrReport) return;
    const timeoutId = setTimeout(() => {
      loadHrReport(activeHrReport, hrReportPage, hrReportSearch, hrReportSort);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [activeHrReport, hrReportPage, hrReportSearch, hrReportSort, selectedPayrollRun, loadHrReport]);

  const openHrReport = (reportId) => {
    setActiveHrReport(reportId);
    setHrReportPage(0);
    setHrReportSearch('');
    setHrReportSort({ key: null, dir: 'desc' });
    setHrReportData({ items: [], total: 0 });
  };

  const handleHrSort = (key) => {
    setHrReportSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
    setHrReportPage(0);
  };

  const downloadHrReportCsv = async (reportId) => {
    const dateWindow = getSelectedRunDateWindow();
    const params = new URLSearchParams({ format: 'csv' });
    if (dateWindow && ['attendance-register', 'leave-register'].includes(reportId)) {
      params.set('from_date', dateWindow.fromDate);
      params.set('to_date', dateWindow.toDate);
    }
    if (hrReportSearch) params.set('search', hrReportSearch);
    try {
      await downloadAuthenticatedFile(
        `/api/v1/reports/hr-report/${reportId}?${params.toString()}`,
        `${reportId}.csv`
      );
      setSuccess('Report exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Export failed');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatCellValue = (key, value) => {
    if (value === null || value === undefined) return '-';
    if (['principal', 'outstanding', 'emi'].includes(key)) {
      return '₹' + Number(value).toLocaleString('en-IN', { minimumFractionDigits: 2 });
    }
    if (key === 'days') return Number(value).toLocaleString('en-IN');
    return String(value);
  };

  const KpiCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-2 rounded-lg ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </div>
  );

  const downloadShiftReport = async (reportKey) => {
    const dateWindow = getSelectedRunDateWindow();
    if (!dateWindow) return;
    const reportPathByKey = {
      shiftAttendance: 'shift-attendance-report',
      departmentRoster: 'department-roster-report',
      overtime: 'overtime-report',
      nightShift: 'night-shift-report',
      staffing: 'staffing-coverage-report',
      swap: 'shift-swap-report'
    };
    const reportPath = reportPathByKey[reportKey];
    if (!reportPath) return;
    const url = `/api/v1/reports/${reportPath}?from_date=${dateWindow.fromDate}&to_date=${dateWindow.toDate}&format=csv`;
    try {
      const response = await fetch(buildApiUrl(url), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}` }
      });
      if (!response.ok) {
        const errorMessage = await extractErrorMessage(response, 'Failed to download shift report');
        setError(errorMessage);
        setTimeout(() => setError(''), 3000);
        return;
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${reportPath}_${dateWindow.fromDate}_to_${dateWindow.toDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setSuccess('Shift report downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to download shift report');
      setTimeout(() => setError(''), 3000);
    }
  };

  // Report Card Component
  const ReportCard = ({ title, description, icon: Icon, color, onPreview, onDownload, downloadLabel = "Download PDF" }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      <h3 className="font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      <div className="flex space-x-2">
        {onPreview && (
          <button
            onClick={onPreview}
            className="flex-1 flex items-center justify-center px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-4 h-4 mr-1" />
            Preview
          </button>
        )}
        {onDownload && (
          <button
            onClick={onDownload}
            className="flex-1 flex items-center justify-center px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-1" />
            {downloadLabel}
          </button>
        )}
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-600">Live HR, payroll, attendance, and compliance reporting</p>
        </div>
      </div>

      {/* Dashboard KPIs */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Key Metrics</h2>
        {kpisLoading && !dashboardKpis ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : dashboardKpis ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <KpiCard
              title="Active Employees"
              value={dashboardKpis.active_employees ?? 0}
              subtitle={dashboardKpis.as_on_label || (dashboardKpis.payroll_period ? `As on date: Period ${dashboardKpis.payroll_period}` : 'No approved payroll')}
              icon={Users}
              color="bg-blue-600"
            />
            <KpiCard
              title="Total Salary"
              value={`₹${Number(dashboardKpis.total_salary ?? dashboardKpis.total_payroll ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              subtitle={dashboardKpis.as_on_label || (dashboardKpis.payroll_period ? `As on date: Period ${dashboardKpis.payroll_period}` : 'No approved payroll')}
              icon={IndianRupee}
              color="bg-indigo-600"
            />
            <KpiCard
              title="Total Deductions"
              value={`₹${Number(dashboardKpis.total_deductions ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
              subtitle={dashboardKpis.as_on_label || (dashboardKpis.payroll_period ? `As on date: Period ${dashboardKpis.payroll_period}` : 'No approved payroll')}
              icon={IndianRupee}
              color="bg-purple-600"
            />
          </div>
        ) : null}
      </div>

      {/* Pay Bill Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <h3 className="text-xl font-bold text-gray-900">Pay Bill</h3>

          {/* Department Filter */}
          <div className="flex flex-wrap items-center gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">
                Department
              </label>
              <select
                value={selectedPaybillDept}
                onChange={(e) => setSelectedPaybillDept(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-medium focus:ring-2 focus:ring-emerald-500 focus:outline-none bg-white min-w-[150px]"
              >
                <option value="ALL">All Departments</option>
                {departmentsList.map((d) => (
                  <option key={d.id} value={d.code || d.name}>
                    {d.name} {d.code && d.code !== d.name ? `(${d.code})` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
          {/* Paybill Report Card */}
          <ReportCard
            title="Paybill Report"
            description="Pune Cantonment Board official monthly paybill & payslip statement with earnings, deductions breakdown and totals."
            icon={FileText}
            color="bg-emerald-600"
            onPreview={() => setViewingPayslip(true)}
          />

          {/* Paybill Summary Card */}
          <ReportCard
            title="Paybill Summary"
            description="Pune Cantonment Board department-wise paybill summary report with overall earnings, deductions and net pay totals."
            icon={BarChart3}
            color="bg-blue-600"
            onPreview={() => setViewingPaybillSummary(true)}
          />

          {/* Grand Summary Card */}
          <ReportCard
            title="Grand Summary"
            description="Pune Cantonment Board grand summary statement of paybills showing overall department-wise breakdown and totals."
            icon={FileSpreadsheet}
            color="bg-purple-600"
            onPreview={() => setViewingGrandSummary(true)}
          />

          {/* Pay slip/Wage slip Card */}
          <ReportCard
            title="Pay slip/Wage slip"
            description="Pune Cantonment Board official Wage Slip Under M. W. Act statement preview with earnings, deductions breakdown and totals."
            icon={FileText}
            color="bg-teal-600"
            onPreview={() => setViewingWageSlip(true)}
          />

          {/* Bank Loan Summary Card */}
          <ReportCard
            title="Bank Loan Summary"
            description="Pune Cantonment Board official monthly bank loan & patsanstha summary statement by department and bank."
            icon={Landmark}
            color="bg-emerald-700"
            onPreview={() => setViewingBankLoanSummary(true)}
          />
        </div>
      </div>

      {/* Head Wise Report Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4 mt-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Head Wise Report</h3>
            <p className="text-xs text-gray-500 mt-0.5">Budget head-wise reports across all departments</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
          {/* Head Wise Summary Card */}
          <ReportCard
            title="Head Wise Summary"
            description="Pune Cantonment Board budget head-wise summary report for all departments."
            icon={BarChart3}
            color="bg-purple-600"
            onPreview={() => setViewingHeadWiseSummary(true)}
          />

          {/* Head Wise Report Card */}
          <ReportCard
            title="Head Wise Report"
            description="Pune Cantonment Board budget head-wise breakdown report across all sections."
            icon={FileText}
            color="bg-indigo-600"
            onPreview={() => setViewingHeadWiseReport(true)}
          />
        </div>
      </div>

      {/* Nameless Ledger & Broad Sheet Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4 mt-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">PF Ledger & Broad Sheet</h3>
            <p className="text-xs text-gray-500 mt-0.5">Provident fund ledger and broadsheet statements for Cantonment servants</p>
          </div>
        </div>





        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
          {/* Ledger Card */}
          <ReportCard
            title="Ledger"
            description="Pune Cantonment Board employee payroll ledger statement with history."
            icon={ClipboardList}
            color="bg-orange-600"
            onPreview={() => setViewingLedger(true)}
          />

          {/* Broad Sheet Card */}
          <ReportCard
            title="Broad Sheet"
            description="Pune Cantonment Board official Provident Fund Broadsheet account for Cantonment Servants."
            icon={FileText}
            color="bg-amber-600"
            onPreview={() => setViewingBroadSheet(true)}
          />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-4 mt-6">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Reports</h3>
            <p className="text-xs text-gray-500 mt-0.5">PFS and VPF reports for employees</p>
          </div>
        </div>





        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-2">
          {/* Ledger Card */}
          <ReportCard
            title="PFS and VPF Report"
            description="PFS and VPF report for employees with contributions and balances."
            icon={ClipboardList}
            color="bg-orange-600"
            onPreview={() => setViewingPfsVpf(true)}
          />
          <ReportCard
            title="PFA Report"
            description="Provident Fund advance and final settlement loan statement by department."
            icon={FileSpreadsheet}
            color="bg-indigo-600"
            onPreview={() => setViewingPfa(true)}
          />
          <ReportCard
            title="CPF Contribution Statement"
            description="Monthly statement showing 14% employer and 10% employee contributions with PRAN numbers and head-wise subtotals."
            icon={IndianRupee}
            color="bg-teal-600"
            onPreview={() => setViewingCpfContribution(true)}
          />
          <ReportCard
            title="Employees Without PRAN & Absent"
            description="Statement of employees without PRAN numbers, retired not in CRA, and absent from duties."
            icon={Users}
            color="bg-rose-600"
            onPreview={() => setViewingCpfMissingPran(true)}
          />
          <ReportCard
            title="GIS Report"
            description="Monthly GIS contribution report by employee with code, name, department, and amount."
            icon={Users}
            color="bg-purple-600"
            onPreview={() => setViewingGis(true)}
          />
          <ReportCard
            title="SBI GM Report"
            description="Monthly SBI bank remittance report by department with account number and net pay amount."
            icon={BarChart3}
            color="bg-blue-700"
            onPreview={() => setViewingSbiGm(true)}
          />
          <ReportCard
            title="Strength Report"
            description="Monthly employee strength report with designation, head code, department, and bank details."
            icon={ClipboardList}
            color="bg-rose-600"
            onPreview={() => setViewingStrength(true)}
          />
          <ReportCard
            title="Upcoming Retirements"
            description="Report of active employees who are going to retire within a selected month range."
            icon={User}
            color="bg-emerald-600"
            onPreview={() => setViewingRetiredReports(true)}
          />
          <ReportCard
            title="Retired Report"
            description="Report of employees who have already retired in a selected year."
            icon={User}
            color="bg-orange-600"
            onPreview={() => setViewingPastRetiredReports(true)}
          />
          <ReportCard
            title="Service Duration Report"
            description="Detailed service duration of employees based on company joining, designation, or department."
            icon={Clock}
            color="bg-cyan-600"
            onPreview={() => setViewingServiceDuration(true)}
          />
          <ReportCard
            title="Adv Report"
            description="Advance report showing employee advance balances and recoveries."
            icon={FileSpreadsheet}
            color="bg-green-600"
            onPreview={() => setViewingAdvReport(true)}
          />
          <ReportCard
            title="Final Settlement"
            description="Final settlement report showing department-wise totals."
            icon={Briefcase}
            color="bg-teal-600"
            onPreview={() => setViewingFinalSettlement(true)}
          />
          <ReportCard
            title="Salary Payment Summary"
            description="Salary payment statement showing payment particulars, amounts, and cheque details."
            icon={ClipboardList}
            color="bg-indigo-600"
            onPreview={() => setViewingSalaryPaymentSummary(true)}
          />
          <ReportCard
            title="FD Interest"
            description="Interest received on account of provident fund fixed deposits."
            icon={IndianRupee}
            color="bg-emerald-600"
            onPreview={() => setViewingFdInterest(true)}
          />
        </div>
      </div>

      {/* Paybill Report Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingPayslip && (
          <div className="fixed inset-0 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed top-0 left-0 w-[100vw] h-[100vh] bg-white overflow-hidden flex flex-col"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Paybill Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly paybill & payslip statement preview</p>
                </div>
                <button
                  onClick={() => setViewingPayslip(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / PCB Paybill Sheet */}
              <div className="flex-1 overflow-y-auto p-6">
                <PCBPaybillReportView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                  initialDept={selectedPaybillDept}
                  initialMonth={selectedPaybillMonth}
                  initialYear={selectedPaybillYear}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Paybill Summary Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingPaybillSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Paybill Summary Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly paybill department summary statement preview</p>
                </div>
                <button
                  onClick={() => setViewingPaybillSummary(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / PCB Paybill Summary */}
              <div className="flex-1 overflow-y-auto p-6">
                <PCBPaybillSummaryView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                  initialDept={selectedPaybillDept}
                  initialMonth={selectedPaybillMonth}
                  initialYear={selectedPaybillYear}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Grand Summary Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingGrandSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-[95vw] w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - GRAND SUMMARY Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly grand summary of paybills statement preview</p>
                </div>
                <button
                  onClick={() => setViewingGrandSummary(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / PCB Grand Summary */}
              <div className="flex-1 overflow-y-auto p-6">
                <PCBGrandSummaryView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                  initialDept={selectedPaybillDept}
                  initialMonth={selectedPaybillMonth}
                  initialYear={selectedPaybillYear}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Ledger Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingLedger && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Ledger Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly payroll ledger preview</p>
                </div>
                <button
                  onClick={() => setViewingLedger(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / PCB Ledger */}
              <div className="flex-1 overflow-y-auto p-6">
                <PCBLedgerView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Broad Sheet Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingBroadSheet && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Broad Sheet Preview</h2>
                  <p className="text-xs text-gray-500">Cantonment Servants Provident Fund Account Broad Sheet statement preview</p>
                </div>
                <button
                  onClick={() => setViewingBroadSheet(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / PCB Broad Sheet */}
              <div className="flex-1 overflow-y-auto p-6">
                <PCBBroadSheetView initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : '2024'} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PFS & VPF Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingPfsVpf && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - PFS & VPF Report</h2>
                  <p className="text-xs text-gray-500">Provident Fund Subscription and Voluntary Provident Fund statement preview</p>
                </div>
                <button
                  onClick={() => setViewingPfsVpf(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close PFS and VPF preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                <PfsVpfReportView initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : '2024'} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Pay slip / Wage slip Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingWageSlip && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Wage Slip Under M. W. Act Preview</h2>
                  <p className="text-xs text-gray-500">Official employee Wage Slip Under M. W. Act statement preview</p>
                </div>
                <button
                  onClick={() => setViewingWageSlip(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / PCB Wage Slip View */}
              <div className="flex-1 overflow-y-auto p-6">
                <PCBWageSlipView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                  initialDept={selectedPaybillDept}
                  initialMonth={selectedPaybillMonth}
                  initialYear={selectedPaybillYear}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* PFA Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingPfa && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200">
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div><h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - PFA Report</h2><p className="text-xs text-gray-500">Provident Fund advance and final settlement loan statement preview</p></div>
                <button onClick={() => setViewingPfa(false)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors" aria-label="Close PFA preview"><X className="w-6 h-6" /></button>
              </div>
              <div className="flex-1 overflow-y-auto p-6"><PfaReportView initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : '2024'} /></div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CPF Contribution Statement Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingCpfContribution && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - CPF Contribution Statement</h2>
                  <p className="text-xs text-gray-500">Monthly CPF list showing 14% employer &amp; 10% employee contributions with head-wise subtotals</p>
                </div>
                <button
                  onClick={() => setViewingCpfContribution(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close CPF contribution preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <CpfContributionReportView
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialDepartment={selectedPaybillDept}
                  departmentsList={departmentsList}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Employees Without PRAN & Absent Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingCpfMissingPran && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Employees Without PRAN &amp; Absent Report</h2>
                  <p className="text-xs text-gray-500">Statement of employees without PRAN numbers, retired not in CRA, and absent from duties</p>
                </div>
                <button
                  onClick={() => setViewingCpfMissingPran(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close missing PRAN preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <CpfMissingPranReportView
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialDepartment={selectedPaybillDept}
                  departmentsList={departmentsList}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* CPF Legacy / Tabbed Pop-up / Modal Preview (Fallback) */}
      <AnimatePresence>
        {viewingCpf && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - CPF Reports</h2>
                  <p className="text-xs text-gray-500">Monthly CPF reports preview</p>
                </div>
                <button
                  onClick={() => setViewingCpf(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close CPF preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <CpfReportView
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialDepartment={selectedPaybillDept}
                  departmentsList={departmentsList}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* GIS Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingGis && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - GIS Report</h2>
                  <p className="text-xs text-gray-500">Monthly Group Insurance Scheme contribution statement preview</p>
                </div>
                <button
                  onClick={() => setViewingGis(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close GIS preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <GisReportView
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialDepartment={selectedPaybillDept}
                  departmentsList={departmentsList}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* SBI GM Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingSbiGm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - SBI GM Report</h2>
                  <p className="text-xs text-gray-500">Monthly SBI bank remittance statement preview</p>
                </div>
                <button
                  onClick={() => setViewingSbiGm(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close SBI GM preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <SbiGmReportView
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialDepartment={selectedPaybillDept}
                  departmentsList={departmentsList}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Strength Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingStrength && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Strength Report</h2>
                  <p className="text-xs text-gray-500">Monthly employee strength statement preview</p>
                </div>
                <button
                  onClick={() => setViewingStrength(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Strength preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <StrengthReportView
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialDepartment={selectedPaybillDept}
                  departmentsList={departmentsList}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Head Wise Summary Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingHeadWiseSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Head Wise Summary Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly head-wise salary statement preview</p>
                </div>
                <button
                  onClick={() => setViewingHeadWiseSummary(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / Head Wise Report View */}
              <div className="flex-1 overflow-y-auto p-6">
                <HeadWiseReportView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                  initialMonth={selectedPaybillMonth}
                  initialYear={selectedPaybillYear}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Head Wise Report Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingHeadWiseReport && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Head Wise Report Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly head-wise detail breakdown statement preview</p>
                </div>
                <button
                  onClick={() => setViewingHeadWiseReport(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content / Head Wise Report Detail View */}
              <div className="flex-1 overflow-y-auto p-6">
                <HeadWiseReportDetailView
                  approvedRuns={approvedRuns}
                  initialRunId={selectedPayrollRun}
                  initialMonth={selectedPaybillMonth}
                  initialYear={selectedPaybillYear}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Retired Reports Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingRetiredReports && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Upcoming Retirements</h2>
                  <p className="text-xs text-gray-500">Report of active employees retiring in the selected month range</p>
                </div>
                <button
                  onClick={() => setViewingRetiredReports(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Upcoming Retirements preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <RetiredEmployeesReportView />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Past Retired Reports Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingPastRetiredReports && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Retired Employees Report</h2>
                  <p className="text-xs text-gray-500">Report of already retired employees by year</p>
                </div>
                <button
                  onClick={() => setViewingPastRetiredReports(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Retired Employees preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <PastRetiredEmployeesReportView />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Service Duration Report Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingServiceDuration && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Service Duration Report</h2>
                  <p className="text-xs text-gray-500">Analyze employee tenure based on company joining, current designation, or department</p>
                </div>
                <button
                  onClick={() => setViewingServiceDuration(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Service Duration preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <ServiceDurationReportView />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Adv Report Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingAdvReport && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Adv Report</h2>
                  <p className="text-xs text-gray-500">Advance report preview</p>
                </div>
                <button
                  onClick={() => setViewingAdvReport(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Adv Report preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <AdvReportView />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Final Settlement Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingFinalSettlement && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Final Settlement</h2>
                  <p className="text-xs text-gray-500">Final settlement statement preview</p>
                </div>
                <button
                  onClick={() => setViewingFinalSettlement(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Final Settlement preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <FinalSettlementView departmentsList={departmentsList} />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Salary Payment Summary Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingSalaryPaymentSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Salary Payment Summary</h2>
                  <p className="text-xs text-gray-500">Salary payment statement preview</p>
                </div>
                <button
                  onClick={() => setViewingSalaryPaymentSummary(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close Salary Payment Summary preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <SalaryPaymentSummaryView />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* FD Interest Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingFdInterest && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-7xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - FD Interest</h2>
                  <p className="text-xs text-gray-500">FD interest report preview</p>
                </div>
                <button
                  onClick={() => setViewingFdInterest(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                  aria-label="Close FD Interest preview"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-0">
                <FdInterestReportView />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Bank Loan Summary Pop-up / Modal Preview */}
      <AnimatePresence>
        {viewingBankLoanSummary && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-2xl max-w-6xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-gray-200"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Pune Cantonment Board - Bank Loan Summary Preview</h2>
                  <p className="text-xs text-gray-500">Official monthly bank loan & patsanstha summary statement preview</p>
                </div>
                <button
                  onClick={() => setViewingBankLoanSummary(false)}
                  className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50">
                <BankLoanSummaryReportView
                  departmentsList={departmentsList}
                  initialDepartment={selectedPaybillDept}
                  initialMonth={selectedPaybillMonth !== 'ALL' ? selectedPaybillMonth : String(new Date().getMonth() + 1)}
                  initialYear={selectedPaybillYear !== 'ALL' ? selectedPaybillYear : String(new Date().getFullYear())}
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Reports;
